import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LanguageIcon from '@mui/icons-material/Language'
import PhoneIcon from '@mui/icons-material/Phone'
import TextFormatIcon from '@mui/icons-material/TextFormat'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { marked } from 'marked'

import { apiZodios } from '~api/apiZodios'
import { CloseIcon, InfoBubbleIcon, LinkIcon } from '~assets/icons'
import { Banner, formatPhoneNumber } from '~components/common'
import { IS_DEMO, IS_PROD } from '~config'
import { lightTheme } from '~createTheme'
import PageNotFoundScreen from '~pages/PageNotFoundScreen'
import { RootState } from '~redux/rootReducer'
import {
  AIInteractionLog,
  AIInteractionTypeEnum,
  ChatDisplaySource,
  IChatMessageDisplay,
  Organization,
  OrganizationAISettings0,
  QueryAnalysis,
  ResponseTypeEnum,
} from '~types'

import { AIInteractionTypeChip } from './AIInteractionTypeChip'
import { MessageBubble } from './MessageBubble'

const aiAnswerTypes = [ResponseTypeEnum.Enum.ANSWER, ResponseTypeEnum.Enum.REQUEST_LOCATION]

function hasSourcesField(obj: unknown): obj is { sources: unknown[] } {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'sources' in obj &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Array.isArray((obj as any).sources)
  )
}

function hasQueryAnalysisField(obj: unknown): obj is { query_analysis: string } {
  if (!obj || typeof obj !== 'object') return false
  const metadata = obj as Record<string, unknown>
  return typeof metadata.query_analysis === 'string'
}

function isChatDisplaySource(obj: unknown): obj is ChatDisplaySource {
  if (!obj || typeof obj !== 'object') return false

  const source = obj as Partial<ChatDisplaySource>
  return (
    typeof source.organization_name === 'string' &&
    typeof source.title === 'string' &&
    typeof source.text === 'string' &&
    typeof source.relevance_score === 'number' &&
    typeof source.source_type === 'string' &&
    typeof source.external_link === 'boolean' &&
    typeof source.external_source === 'boolean' &&
    typeof source.is_linked_organization === 'boolean' &&
    typeof source.source_id === 'string'
  )
}

function isQueryAnalysis(obj: unknown): obj is QueryAnalysis {
  if (!obj || typeof obj !== 'object') return false

  const analysis = obj as Partial<QueryAnalysis>
  return (
    typeof analysis.query_type === 'string' &&
    typeof analysis.is_context_dependent === 'boolean' &&
    typeof analysis.primary_keyword === 'string' &&
    typeof analysis.rewritten_query === 'string' &&
    Array.isArray(analysis.target_organization_references)
  )
}

export const convertToLogMessages = (
  aiInteractionLog: AIInteractionLog,
  organization: Organization,
  useEnglish: boolean,
): IChatMessageDisplay[] => {
  const messages: IChatMessageDisplay[] = []

  // Add query messages
  aiInteractionLog.queries.forEach((query) => {
    let queryAnalysis: QueryAnalysis | null = null

    if (hasQueryAnalysisField(query.metadata)) {
      try {
        const parsedAnalysis = JSON.parse(query.metadata.query_analysis)
        if (isQueryAnalysis(parsedAnalysis)) {
          queryAnalysis = parsedAnalysis as QueryAnalysis
        }
      } catch (e) {
        console.error('Failed to parse query_analysis field', e)
      }
    }
    messages.push({
      id: query.id || null,
      text: useEnglish && query.english_text ? query.english_text : query.body,
      sender: 'USER',
      timestamp: new Date(query.created || new Date()),
      queryAnalysis: queryAnalysis,
      impression: null,
      sources: [],
    })
  })

  // Add response messages
  aiInteractionLog.responses.forEach((responseWithSources) => {
    const response = responseWithSources.response
    const text = marked.parse(
      useEnglish && response.english_text ? response.english_text : response.body,
    )

    let extractedSources: ChatDisplaySource[] = []

    if (response.metadata) {
      // Safely unpack ChatDisplaySource from unknown JSON field
      if (hasSourcesField(response.metadata)) {
        extractedSources = response.metadata.sources
          .filter(isChatDisplaySource)
          .map((source) => source as ChatDisplaySource)
      }
    }
    messages.push({
      id: response.id || null,
      text: text as string,
      sender:
        response.type && (aiAnswerTypes as string[]).includes(response.type as string)
          ? 'AI'
          : 'ACTION',
      responseType: response.type,
      sources: extractedSources,
      timestamp: new Date(response.created || new Date()),
      impression: response.impression ?? null,
      relevantChunks: responseWithSources.sources,
    })
  })

  return messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

interface AIInteractionContentProps {
  chatId: string
  orgSettings: OrganizationAISettings0
  onClose?: () => void
  showHeader?: boolean
  showOrgInfo?: boolean
}

const AIInteractionContent: React.FC<AIInteractionContentProps> = ({
  chatId,
  orgSettings,
  onClose,
  showHeader = true,
  showOrgInfo = false,
}) => {
  const organization = orgSettings.organization
  const [useEnglish, setUseEnglish] = useState(false)
  const user = useSelector((state: RootState) => state?.auth.user)
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const location = useLocation()
  const [isTestingMode, setIsTestingMode] = useState(false)
  const [aiInteractionLog, setAiInteractionLog] = useState<AIInteractionLog | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const [messages, setMessages] = useState<IChatMessageDisplay[]>([])
  const [showPageNotFound, setShowPageNotFound] = useState(false)
  const [debugVisible, setDebugVisible] = useState<boolean>(true)

  const showDebugInfo =
    isTestingMode || (isSuper && debugVisible) || (!isSuper && !IS_PROD && !IS_DEMO)

  const hasNonEnglishContent = aiInteractionLog
    ? aiInteractionLog.queries.some((q) => q.language && q.language !== 'en') ||
      aiInteractionLog.responses.some((r) => r.response.language && r.response.language !== 'en')
    : false

  const handleLanguageChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: boolean | null,
  ): void => {
    if (newValue !== null) {
      setUseEnglish(newValue)
    }
  }

  const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return
    }
    setLinkCopied(false)
  }

  const handleOnClose = (): void => {
    setMessages([])
    setAiInteractionLog(null)
    if (onClose) {
      onClose()
    }
  }

  const fetchAIInteractionLog = async (chatId: string): Promise<void> => {
    setLoading(true)
    try {
      if (!user || !chatId) return
      const response = await apiZodios.ai_getAIInteractionLog({
        user_id: user.id,
        chat_id: chatId,
      })
      setAiInteractionLog(response.interaction_log)
    } catch (error) {
      console.error('Failed to fetch chat details:', error)
      setShowPageNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const getShareableLink = (): string => {
    // Get shareable link without organization context
    const baseUrl = window.location.origin
    return `${baseUrl}/react/query-log/${chatId}`
  }

  useEffect(() => {
    const hasTestingPath = location.pathname.endsWith('/testing')
    setIsTestingMode(hasTestingPath)
    fetchAIInteractionLog(chatId)
  }, [location, chatId])

  useEffect(() => {
    if (aiInteractionLog) {
      setMessages(convertToLogMessages(aiInteractionLog, organization, useEnglish))
    }
  }, [aiInteractionLog, useEnglish])

  if (showPageNotFound) {
    return <PageNotFoundScreen permissionsError={true} />
  }

  return (
    <>
      {showHeader && (
        <>
          <Stack spacing={2} bgcolor='var(--lightoffwhite)' p={4}>
            <Stack direction='row' justifyContent='space-between'>
              <Stack direction='row' alignItems='center' spacing={1}>
                {showOrgInfo && aiInteractionLog?.organization_image && (
                  <Box
                    component='img'
                    src={aiInteractionLog.organization_image}
                    alt={`${aiInteractionLog.organization_name || 'Organization'} logo`}
                    sx={{
                      height: 40,
                      width: 'auto',
                      maxWidth: 120,
                      objectFit: 'contain',
                    }}
                  />
                )}
                <Typography variant='h1' fontSize='1.5rem' fontWeight='medium'>
                  {`${showOrgInfo && aiInteractionLog?.organization_name ? `${aiInteractionLog.organization_name} ` : ''}Conversation Log`}
                </Typography>
              </Stack>
              <Stack direction='row' alignItems='center' spacing={1}>
                {isSuper && (
                  <FormControlLabel
                    control={
                      <Switch
                        size='small'
                        checked={debugVisible}
                        onChange={(e) => setDebugVisible(e.target.checked)}
                        color='primary'
                      />
                    }
                    label={<Typography variant='label'>Debug Info</Typography>}
                    sx={{ m: 0 }}
                  />
                )}
                <Button
                  onClick={() => {
                    navigator.clipboard
                      .writeText(getShareableLink())
                      .then(() => {
                        setLinkCopied(true)
                      })
                      .catch((err) => console.error('Failed to copy URL: ', err))
                  }}
                  size='small'
                  variant='contained'
                  color='secondary'
                  sx={{ gap: 1 }}
                >
                  <LinkIcon width={15} color={'var(--white)'} />
                  <Typography
                    variant='caption'
                    fontWeight={'bold'}
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  >
                    Copy Link
                  </Typography>
                </Button>
                {onClose && (
                  <IconButton onClick={handleOnClose} sx={{ p: 0 }}>
                    <CloseIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <Stack direction='row' spacing={1}>
              {aiInteractionLog && (
                <AIInteractionTypeChip
                  type={aiInteractionLog.interaction_type}
                  phoneLineType={aiInteractionLog.phone_line_type || ''}
                />
              )}
              {showDebugInfo &&
                aiInteractionLog?.interaction_type === AIInteractionTypeEnum.VOICE &&
                aiInteractionLog?.voice_ai_call_info && (
                  <>
                    <Chip
                      icon={<PhoneIcon sx={{ fontSize: 20 }} />}
                      label={
                        <Typography display='flex' alignItems='center'>
                          {formatPhoneNumber(aiInteractionLog.voice_ai_call_info.from_phone_number)}
                          <ArrowRightAltIcon />
                          {formatPhoneNumber(aiInteractionLog.voice_ai_call_info.to_phone_number)}
                        </Typography>
                      }
                      color='primary'
                      size='small'
                    />
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
                      label={formatDuration(aiInteractionLog.voice_ai_call_info.call_duration)}
                      size='small'
                      sx={{ bgcolor: '#E6F4FA' }}
                    />
                    <Chip
                      label={aiInteractionLog.voice_ai_call_info.call_status}
                      color={
                        aiInteractionLog.voice_ai_call_info.call_status === 'completed'
                          ? 'success'
                          : 'warning'
                      }
                      size='small'
                    />
                  </>
                )}
            </Stack>
          </Stack>
          <Divider />
          {aiInteractionLog?.interaction_type === AIInteractionTypeEnum.VOICE && (
            <Banner
              color={'var(--paleblue)'}
              message={
                <Typography variant='body2'>
                  Transcripts are generated from the original audio with speech recognition
                  technology. Transcript errors may occur.
                </Typography>
              }
              sx={{ color: lightTheme.palette.primary.dark }}
              textAlign='left'
              startIcon={<InfoBubbleIcon color='var(--blue)' />}
              dismissable={false}
            />
          )}
        </>
      )}

      <Box
        sx={{
          padding: '20px',
          flexGrow: 1,
          overflowY: 'auto',
          backgroundColor: 'var(--lightoffwhite)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '400px',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant='body1' sx={{ mt: 2 }}>
              Loading interaction...
            </Typography>
          </Box>
        ) : messages.length > 0 ? (
          messages.map((message, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mb: 0.5,
                  textAlign: 'center',
                }}
              >
                {message.timestamp.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}{' '}
                at{' '}
                {message.timestamp.toLocaleString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric',
                  hour12: true,
                })}
              </Box>
              <MessageBubble
                message={message}
                orgSettings={orgSettings}
                isLog={true}
                showDebugInfo={showDebugInfo}
              />
            </React.Fragment>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
            <Typography variant='body1'>No conversation data available</Typography>
          </Box>
        )}
      </Box>

      {hasNonEnglishContent && !loading && (
        <Paper
          elevation={1}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            borderRadius: '6px',
            padding: '2px',
            backgroundColor: 'white',
          }}
        >
          <ToggleButtonGroup
            value={useEnglish}
            exclusive
            onChange={handleLanguageChange}
            size='small'
            sx={{
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '4px',
                px: 1.5,
                py: 0.25,
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 400,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              },
            }}
          >
            <Tooltip
              title='This is the conversation the constituent saw'
              placement='top'
              enterDelay={400}
            >
              <ToggleButton value={false}>
                <LanguageIcon sx={{ mr: 0.75, fontSize: '18px' }} />
                Original
              </ToggleButton>
            </Tooltip>
            <Tooltip title='Conversation translated to English' placement='top' enterDelay={400}>
              <ToggleButton value={true}>
                <TextFormatIcon sx={{ mr: 0.75, fontSize: '18px' }} />
                English
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Paper>
      )}

      <Snackbar open={linkCopied} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert
          icon={<CheckCircleIcon />}
          onClose={handleCloseAlert}
          severity='success'
          sx={{ width: '100%' }}
        >
          Shareable log link copied
        </Alert>
      </Snackbar>
    </>
  )
}

export default AIInteractionContent
